import { type FC } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import { useMarketingQuotaUsageQuery } from "@/partner/entities/marketing";
import { interpolationFormat } from "@/partner/shared/lib";
import type { BroadcastFormState } from "../model/broadcastForm";

interface BroadcastScheduleCardProps {
  form: BroadcastFormState;
  onChange: (patch: Partial<BroadcastFormState>) => void;
  disabled: boolean;
  /** Отправка уже началась (SENDING, SENT, архив после отправки): только время старта. */
  startedAt: string | null;
}

/** Когда отправить: сейчас или в дату и время; ниже остаток квоты писем. */
export const BroadcastScheduleCard: FC<BroadcastScheduleCardProps> = ({
  form,
  onChange,
  disabled,
  startedAt,
}) => {
  const { t, i18n } = useTranslation();
  const { data } = useMarketingQuotaUsageQuery({
    fetchPolicy: "cache-and-network",
    // Отправка уже началась: квота этой карточке больше не нужна, а
    // запрос без skip всё равно уходил бы на каждый рендер read-only вида.
    skip: Boolean(startedAt),
  });
  const usage = data?.marketingQuotaUsage;
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const isPast =
    !disabled &&
    form.sendMode === "at" &&
    Boolean(form.scheduledAt && form.scheduledAt.isBefore(moment()));

  if (startedAt) {
    return (
      <Card elevation={0}>
        <CardHeader
          title={t("broadcasts.schedule.title")}
          sx={{ px: 2, pt: 2, pb: 0 }}
        />
        <CardContent sx={{ px: 2, pb: 2, pt: 2 }}>
          <Stack spacing={0.5}>
            <Typography variant="body2">
              {t("broadcasts.schedule.startedAt", {
                // "dateTime" (DD.MM.YYYY HH:mm): тот же форматтер, что и у
                // остальных точных меток времени события в кабинете
                // (PurchaseDetailsDialog, ChatBansList, SupportRequestDialog
                // и т.д.). "short" даёт только дату и не отличит отправку в
                // 00:05 от отправки в 23:55.
                date: interpolationFormat(startedAt, "dateTime", i18n.language),
              })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("broadcasts.schedule.timezoneHint", { zone })}
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card elevation={0}>
      <CardHeader
        title={t("broadcasts.schedule.title")}
        sx={{ px: 2, pt: 2, pb: 0 }}
      />
      <CardContent sx={{ px: 2, pb: 2, pt: 2 }}>
        <Stack spacing={2}>
          <RadioGroup
            row
            value={form.sendMode}
            onChange={(event) => {
              const sendMode = event.target.value === "at" ? "at" : "now";
              // Дата по умолчанию: ближайший ровный час плюс два часа, а не
              // плюс один час от текущей минуты, иначе у самого края часа
              // (например, в 14:59) получался запас меньше минуты до
              // «дата уже прошла».
              onChange(
                sendMode === "at" && !form.scheduledAt
                  ? {
                      sendMode,
                      scheduledAt: moment().startOf("hour").add(2, "hours"),
                    }
                  : { sendMode }
              );
            }}
          >
            <FormControlLabel
              value="now"
              control={<Radio />}
              label={t("broadcasts.schedule.now")}
              disabled={disabled}
            />
            <FormControlLabel
              value="at"
              control={<Radio />}
              label={t("broadcasts.schedule.at")}
              disabled={disabled}
            />
          </RadioGroup>
          {form.sendMode === "at" && (
            <>
              <DateTimePicker
                label={t("broadcasts.schedule.dateTime")}
                value={form.scheduledAt}
                onChange={(scheduledAt) => onChange({ scheduledAt })}
                disabled={disabled}
                disablePast
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: isPast,
                    helperText: isPast
                      ? t("broadcasts.schedule.pastHint")
                      : undefined,
                  },
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {t("broadcasts.schedule.timezoneHint", {
                  zone,
                })}
              </Typography>
            </>
          )}
          {usage && (
            <Typography variant="caption" color="text.secondary">
              {t("broadcasts.schedule.quotaHint", {
                left: Math.max(0, usage.quota - usage.sent),
                quota: usage.quota,
              })}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
